import React, { useState } from 'react';
import isEmpty from 'lodash/isEmpty';

import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import { logPostHogEvent } from '@utils/index';
import trackUse from '@utils/trackUse';
import { useSignUpModal } from '@hooks/useLoginModal';
import { useGetUserData } from '@hooks/useGetUserData';
import config from '@configFile';
import { OptionType, VariationType } from '@types';
import Share from '@pages/Variations/Share';
import LimitMessage from '@pages/Variations/components/LimitMessage';
import VariationsTable from '@pages/Variations/VariationsTable';
import DisclaimerAccordion from '@pages/Variations/DisclaimerAccordion';
import SizeSelect from '@pages/Variations/components//SizeSelect';
import {
  getVariantInfo,
  ALL_SIZE_FILTER,
  exportVariations
} from '@pages/Variations/utils';

const VariationsContent = ({
  variations,
  ASIN
}: {
  variations: VariationType[];
  ASIN: string;
}) => {
  const { data: userData } = useGetUserData();

  const [sizeFilter, setSizeFilter] = useState<OptionType>(ALL_SIZE_FILTER);
  const { showNonLoggedInModal } = useSignUpModal();

  const filteredVariations = variations.filter((variation) => {
    if (sizeFilter.value === 'ALL') {
      return true;
    }

    return variation.variants?.size_name === sizeFilter.value;
  });

  const handleDownloadClick = () => {
    if (!userData) {
      showNonLoggedInModal({
        onLoginCBFn: () => {
          handleDownloadClickLogic(false);
        },
        featureName: 'variations-download-data'
      });
      return;
    }

    handleDownloadClickLogic(true);
  };

  const handleDownloadClickLogic = (checkVerify: boolean) => {
    if (!userData?.hasVerifiedEmail && checkVerify) {
      // eslint-disable-next-line no-alert
      alert('Verify your email to download CSVs');
      return;
    }

    const fileName = `Variations for ${variations?.[0]?.title}` || ASIN;
    trackUse({
      item: 'variations-download-data',
      value: `${fileName}`,
      type: 'DOWNLOAD'
    });

    logPostHogEvent('variations-download-csv-data', {
      value: `${fileName}`,
      type: 'DOWNLOAD'
    });

    exportVariations(variations, fileName);
  };

  const renderActions = () => {
    const isShareAPISupported = navigator.share;

    return (
      <Box display="flex" justifyContent="space-between" marginTop="8">
        {!isEmpty(variations) && ASIN && (
          <>
            <Tooltip
              title="Download a CSV of the data on this query including premium data points. Available for logged in users."
              aria-label="copy"
              placement="top-end"
            >
              <span>
                <Button
                  onClick={handleDownloadClick}
                  color="primary"
                  variant="outlined"
                  size="small"
                  data-testid="csv-download-button"
                  endIcon={<CloudDownloadIcon />}
                  sx={{
                    marginTop: '10px',
                    // if disabled
                    '&:disabled': {
                      opacity: 0.9
                    }
                  }}
                >
                  Download CSV
                </Button>
              </span>
            </Tooltip>
          </>
        )}
        {!isEmpty(variations) && ASIN && isShareAPISupported && (
          <Share
            url={`${window?.location?.origin}${window?.location?.pathname}?ASIN=${ASIN}`}
            title={`Variations for ${variations?.[0]?.title || ASIN}`}
          />
        )}
      </Box>
    );
  };

  const renderLimitMessage = () => {
    const needsEmailVerification = !!userData?.needsEmailVerification;
    if (
      (!userData || needsEmailVerification) &&
      variations?.length === config.NON_USER_VARIATIONS_LIMIT
    ) {
      return <LimitMessage needsEmailVerification={needsEmailVerification} />;
    }
    return null;
  };

  const variationsInfo = getVariantInfo(variations);
  const { sizeOptions } = variationsInfo;
  return (
    <>
      {renderActions()}
      <SizeSelect
        options={sizeOptions}
        value={sizeFilter}
        onChange={setSizeFilter}
      />
      {renderLimitMessage()}
      <Box marginTop="10px">
        <VariationsTable data={filteredVariations} />
      </Box>
      <DisclaimerAccordion />
    </>
  );
};

export default VariationsContent;
